import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { Holiday } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';

const repo = getTurnoGeoRepository();
const DAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const dayName = (date: string) => DAYS[new Date(`${date}T00:00:00Z`).getUTCDay()];
const fmt = (date: string) => new Date(`${date}T00:00:00Z`).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });

/**
 * Configuración > Festivos: el calendario que usa el cálculo de horas para separar lo ordinario de lo festivo.
 * Viene cargado 2025, 2026 y 2027 (Colombia). "Generar" agrega los de otro año; todo se puede ajustar a mano.
 * El domingo cuenta como festivo por el parámetro SUNDAY_IS_FESTIVE (no necesita estar en esta lista).
 */
export function HolidaysTab() {
  const toast = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ date: '', name: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<Holiday | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const load = () => {
    setIsLoading(true);
    repo.listHolidays(year).then((r) => { setRows(r); setIsLoading(false); }).catch((e) => { setIsLoading(false); toast.show(e instanceof Error ? e.message : 'No se pudieron cargar los festivos.', 'error'); });
  };
  useEffect(load, [year]);

  const openCreate = () => { setForm({ date: `${year}-01-01`, name: '' }); setCreating(true); };
  const openEdit = (h: Holiday) => { setForm({ date: h.date, name: h.name }); setEditing(h); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    if (!form.name.trim()) { toast.show('El nombre es obligatorio.', 'error'); return; }
    setIsSaving(true);
    try {
      if (editing) { await repo.updateHoliday(editing.id, { name: form.name }); toast.show('Festivo actualizado.'); }
      else { await repo.createHoliday({ date: form.date, name: form.name }); toast.show('Festivo agregado.'); }
      close();
      load();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo guardar.', 'error'); }
    finally { setIsSaving(false); }
  };
  const toggle = async (h: Holiday) => {
    try { await repo.updateHoliday(h.id, { isActive: !h.isActive }); load(); } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo cambiar.', 'error'); }
  };
  const generate = async () => {
    setIsGenerating(true);
    try {
      const r = await repo.generateHolidays(year);
      toast.show(r.created > 0 ? `Se agregaron ${r.created} festivos de ${year}.` : `Ya estaban todos los festivos de ${year}.`);
      load();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudieron generar.', 'error'); }
    finally { setIsGenerating(false); }
  };

  const columns: Column<Holiday>[] = [
    { key: 'date', filterValue: (h) => h.date, header: 'Fecha', render: (h) => <span className="font-medium">{fmt(h.date)}</span>, sortable: true, sortValue: (h) => h.date },
    { key: 'dow', filterValue: (h) => dayName(h.date), header: 'Día', render: (h) => dayName(h.date) },
    { key: 'name', header: 'Festivo', render: (h) => h.name },
    {
      key: 'estado', header: 'Estado',
      render: (h) => (
        <button onClick={() => toggle(h)} title="Activar / desactivar">
          <Badge tone={h.isActive ? 'success' : 'neutral'}>{h.isActive ? 'Activo' : 'Inactivo (no cuenta)'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions', header: '',
      render: (h) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar nombre" onClick={() => openEdit(h)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(h)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => setYear((y) => y - 1)} aria-label="Año anterior">‹</Button>
        <span className="min-w-[4.5rem] text-center text-lg font-bold text-ink-900">{year}</span>
        <Button variant="outline" onClick={() => setYear((y) => y + 1)} aria-label="Año siguiente">›</Button>
        <Badge tone="info">{rows.filter((r) => r.isActive).length} festivos activos</Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>Agregar festivo</Button>
        <Button variant="outline" onClick={generate} isLoading={isGenerating}>Generar festivos de {year}</Button>
      </div>
      <p className="flex items-start gap-2 text-xs text-ink-500">
        <InfoTip title="¿Cómo se usan?" text="Cada hora trabajada un festivo (o un domingo) se separa de las horas ordinarias para liquidar el recargo dominical/festivo y las extras festivas. Si desactivas un festivo, ese día se trata como ordinario. Los domingos cuentan solos (parámetro SUNDAY_IS_FESTIVE)." />
        <span>El domingo cuenta como festivo automáticamente. Aquí van los festivos del calendario. Generar usa la Ley Emiliani (Ley 51 de 1983) y Semana Santa; revísalos contra la norma del año.</span>
      </p>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="📅" title={`Sin festivos en ${year}`} description='Pulsa "Generar festivos" para cargar los de Colombia.' />
      ) : (
        <DataTable excel fixedKey="date" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} defaultSortKey="date" defaultSortDir="asc" emptyTitle="Sin festivos" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar festivo' : 'Agregar festivo'}>
        <div className="space-y-3">
          <Input label="Fecha" type="date" value={form.date} disabled={!!editing} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Input label="Nombre" value={form.name} maxLength={150} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar festivo"
        description={`¿Eliminar "${deleting?.name}" (${deleting ? fmt(deleting.date) : ''})? Ese día se liquidará como ordinario.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) { try { await repo.deleteHoliday(deleting.id); } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo eliminar.', 'error'); } }
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
